import type React from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTokens(tokens: number): string {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ExecutionTreeNodeStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'stopped';

export interface ExecutionTreeNode {
  id: string;
  label: string;
  subtitle?: string;
  contextTokens?: number;
  status?: ExecutionTreeNodeStatus;
  badge?: string;
  children?: ExecutionTreeNode[];
  metadata?: Record<string, unknown>;
}

export interface ExecutionTreeProps {
  nodes: ExecutionTreeNode[];
  onNodeClick?: (nodeId: string) => void;
  expandedNodeId?: string;
  renderNodeDetail?: (node: ExecutionTreeNode) => React.ReactNode;
  collapsedNodeIds?: Set<string>;
  onToggleCollapse?: (nodeId: string) => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Status icons (12x12 inline SVGs)
// ---------------------------------------------------------------------------

function statusIcon(status: ExecutionTreeNodeStatus | undefined) {
  switch (status) {
    case 'completed':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-green-500">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
    case 'running':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 animate-spin text-blue-500">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      );
    case 'failed':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-red-500">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      );
    case 'stopped':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-amber-500">
          <rect x="6" y="6" width="12" height="12" rx="1" />
        </svg>
      );
    case 'skipped':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
          <path d="M5 12h14" />
        </svg>
      );
    case 'pending':
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}

// ---------------------------------------------------------------------------
// Collapse chevron (10x10 SVG triangle)
// ---------------------------------------------------------------------------

function CollapseChevron({ collapsed, onClick }: { collapsed: boolean; onClick: (e: React.MouseEvent) => void }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className="shrink-0 text-muted-foreground cursor-pointer hover:text-foreground"
      onClick={onClick}
    >
      {collapsed ? (
        <path d="M3 1 L8 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M1 3 L5 8 L9 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ExecutionTreeRow — renders one node + recurses for children
// ---------------------------------------------------------------------------

function ExecutionTreeRow({
  node,
  depth,
  onNodeClick,
  expandedNodeId,
  renderNodeDetail,
  collapsedNodeIds,
  onToggleCollapse,
}: {
  node: ExecutionTreeNode;
  depth: number;
  onNodeClick?: (nodeId: string) => void;
  expandedNodeId?: string;
  renderNodeDetail?: (node: ExecutionTreeNode) => React.ReactNode;
  collapsedNodeIds?: Set<string>;
  onToggleCollapse?: (nodeId: string) => void;
}) {
  const hasSubContent = node.subtitle || node.contextTokens != null;
  const isExpanded = node.id === expandedNodeId;
  const hasChildren = node.children != null && node.children.length > 0;
  const isCollapsed = collapsedNodeIds?.has(node.id) ?? false;

  return (
    <>
      <div
        role="treeitem"
        aria-selected={isExpanded}
        aria-expanded={hasChildren ? !isCollapsed : undefined}
        className={`relative flex flex-col gap-0 text-sm${onNodeClick ? ' cursor-pointer hover:bg-accent/50 rounded-sm' : ''}`}
        style={{ paddingLeft: `${depth * 16}px` }}
        onClick={onNodeClick ? () => onNodeClick(node.id) : undefined}
      >
        {/* Primary row: chevron + icon + label + badges */}
        <div className="flex items-center gap-1.5 h-6">
          {hasChildren && onToggleCollapse ? (
            <CollapseChevron
              collapsed={isCollapsed}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse(node.id);
              }}
            />
          ) : (
            <span className="shrink-0" style={{ width: '10px' }} />
          )}
          {statusIcon(node.status)}
          <span className="truncate">{node.label}</span>
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            {node.contextTokens != null && (
              <span className="text-[10px] text-muted-foreground/70">{formatTokens(node.contextTokens)}</span>
            )}
            {node.badge && (
              <span className="text-xs text-muted-foreground">{node.badge}</span>
            )}
          </div>
        </div>

        {/* Subtitle row */}
        {hasSubContent && node.subtitle && (
          <div className="flex items-center h-4 pl-[18px]">
            <span className="truncate text-[10px] text-muted-foreground/70">{node.subtitle}</span>
          </div>
        )}
      </div>
      {/* Inline detail panel — renders right below the clicked node */}
      {isExpanded && renderNodeDetail && (
        <div style={{ paddingLeft: `${(depth + 1) * 16}px` }} className="py-1">
          {renderNodeDetail(node)}
        </div>
      )}
      {!isCollapsed && node.children?.map((child) => (
        <ExecutionTreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          onNodeClick={onNodeClick}
          expandedNodeId={expandedNodeId}
          renderNodeDetail={renderNodeDetail}
          collapsedNodeIds={collapsedNodeIds}
          onToggleCollapse={onToggleCollapse}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// ExecutionTree — main export
// ---------------------------------------------------------------------------

export function ExecutionTree({ nodes, onNodeClick, expandedNodeId, renderNodeDetail, collapsedNodeIds, onToggleCollapse, className }: ExecutionTreeProps) {
  return (
    <div className={className} role="tree">
      {nodes.map((node) => (
        <ExecutionTreeRow
          key={node.id}
          node={node}
          depth={0}
          onNodeClick={onNodeClick}
          expandedNodeId={expandedNodeId}
          renderNodeDetail={renderNodeDetail}
          collapsedNodeIds={collapsedNodeIds}
          onToggleCollapse={onToggleCollapse}
        />
      ))}
    </div>
  );
}
